/** @jsx jsx */

import { ComponentProps, ReactNode } from 'react';

import { Colors, Icon, Text } from '@zapier/design-system';
import { css, jsx } from '@emotion/core';

import { useAvroEvent } from 'app/developer-v3/hooks';

import NavLink from './NavLink';

type Section = 'insight dashboard' | 'partner program';

const Styles = {
  label: css`
    padding: 6px 30px;
  `,
  root: css`
    padding: 10px;
    :not(:last-of-type) {
      border-bottom: 1px solid ${Colors.GrayWarm4};
    }
  `,
  text: css`
    flex: 1;
  `,
  wrapper: (isActive: boolean) => [
    css`
      align-items: center;
      border-radius: 3px;
      display: flex;
      padding-left: 10px;
      :hover {
        background-color: ${Colors.GrayWarm2};
      }
    `,
    isActive &&
      css`
        background-color: ${Colors.UiPrimaryWeakest};
        font-weight: 600;
        :hover {
          background-color: ${Colors.UiPrimaryWeakest};
        }
      `,
  ],
};

type Props = {
  badge?: ReactNode;
  iconName: ComponentProps<typeof Icon>['name'];
  isActive: boolean;
  isEnabled: boolean;
  label: string;
  section: Section;
  tooltip?: string;
  url?: string;
};

const Item = ({
  badge,
  iconName,
  isActive,
  isEnabled,
  label,
  section,
  tooltip,
  url,
}: Props) => {
  const { emitPartnerUserInteractionEvent } = useAvroEvent();

  return (
    <div css={Styles.root}>
      <div css={Styles.wrapper(isActive)}>
        <Icon name={iconName} size={24} />
        <div css={Styles.text}>
          <Text fontWeight={600} tag="h3" type="Body4">
            <div>
              {url === '' ? (
                <span css={Styles.label}>{label}</span>
              ) : (
                <NavLink
                  badge={badge}
                  gridTemplateColumn={badge && '1fr auto'}
                  hasLeftIndent={false}
                  href={url}
                  isActive={isActive}
                  isLocked={!isEnabled}
                  label={label}
                  onClick={() =>
                    emitPartnerUserInteractionEvent({
                      interactionGoal:
                        'Navigate to a section in the developer platform UI',
                      interactionName: 'sidebar_item_clicked',
                      interactionRawText: label,
                      eventAction: 'click',
                      optionSelected: `${section} - ${label.toLowerCase()}`,
                    })
                  }
                  tag="div"
                  tooltip={tooltip}
                />
              )}
            </div>
          </Text>
        </div>
      </div>
    </div>
  );
};

export default Item;
